import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { STAGES, QUARTERS, stageChipClass } from "@/lib/constants";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { MultiSelect } from "@/components/MultiSelect";

export default function Opportunities() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stageFilter = searchParams.get("stage") || "";
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("kanban");
  const empty = {
    name: "", account_id: "", description: "", value: 0, product_ids: [],
    closure_date: "", quarter: "Q1", stage: "Pipeline", owner_id: user?.id,
    project_team: [], shared_with: [],
  };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/opportunities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { setTab(stageFilter ? "list" : "kanban"); }, [stageFilter]);
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/products", { params: { status: "active" } }).then((r) => setProducts(r.data));
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data));
  }, []);

  const save = async () => {
    if (!form.name || !form.account_id) return toast.error("Name and account are required");
    try {
      const r = await api.post("/opportunities", { ...form, value: Number(form.value) || 0 });
      toast.success("Created");
      setOpen(false); setForm(empty); load();
      navigate(`/opportunities/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const grouped = STAGES.map((s) => ({ stage: s, items: items.filter((it) => it.stage === s) }));
  const filteredList = stageFilter ? items.filter((o) => o.stage === stageFilter) : items;

  return (
    <div data-testid="opportunities-page">
      <PageHeader title="Opportunities" description="Sales pipeline & deal stages." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setForm({ ...empty, owner_id: user?.id })} className="btn-brand" data-testid="opportunity-new"><Plus size={16} className="mr-1.5" />New Opportunity</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Opportunity</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field></div>
              <Field label="Account *">
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Value (₹)"><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></Field>
              <Field label="Quarter *">
                <Select value={form.quarter} onValueChange={(v) => setForm({ ...form, quarter: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Stage">
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Closure Date"><Input type="date" value={form.closure_date || ""} onChange={(e) => setForm({ ...form, closure_date: e.target.value })} /></Field>
              <Field label="Owner">
                <Select value={form.owner_id || user?.id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Products">
                  <MultiSelect options={products.map((p) => ({ value: p.id, label: p.name }))} value={form.product_ids} onChange={(v) => setForm({ ...form, product_ids: v })} placeholder="Select products" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Project Team">
                  <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={form.project_team} onChange={(v) => setForm({ ...form, project_team: v })} placeholder="Assign team members" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Share With (extra visibility)">
                  <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={form.shared_with} onChange={(v) => setForm({ ...form, shared_with: v })} placeholder="Share with users" />
                </Field>
              </div>
              <div className="sm:col-span-2"><Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="btn-brand" data-testid="opportunity-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      } />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="kanban" data-testid="opp-tab-kanban">Pipeline</TabsTrigger>
          <TabsTrigger value="list" data-testid="opp-tab-list">List</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban" className="mt-4">
          <div className="flex gap-4 overflow-x-auto pb-3">
            {grouped.map((g) => (
              <div key={g.stage} className="flex-shrink-0 w-72 bg-slate-100 rounded-lg p-3 flex flex-col max-h-[75vh]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${stageChipClass(g.stage)}`}>{g.stage}</div>
                    <div className="text-xs text-slate-500 mt-1">{g.items.length} • {fmtINR(g.items.reduce((s, i) => s + (i.value || 0), 0))}</div>
                  </div>
                </div>
                <div className="space-y-2 overflow-y-auto">
                  {g.items.map((it) => (
                    <div key={it.id} className="bg-white border border-slate-200 rounded-md p-3 hover:border-blue-400 transition-colors cursor-pointer"
                      onClick={() => navigate(`/opportunities/${it.id}`)}>
                      <div className="font-medium text-sm text-slate-900 truncate">{it.name}</div>
                      <div className="text-xs text-slate-500 truncate">{accounts.find((a) => a.id === it.account_id)?.company_name || "—"}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-semibold text-slate-900">{fmtINR(it.value)}</span>
                        <span className="text-[10px] text-slate-400">{it.quarter} • {fmtDate(it.closure_date)}</span>
                      </div>
                    </div>
                  ))}
                  {g.items.length === 0 && <div className="text-xs text-slate-400 text-center py-4">Empty</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Account</TableHead><TableHead>Stage</TableHead>
                <TableHead>Value</TableHead><TableHead>Quarter</TableHead><TableHead>Closure</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredList.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/opportunities/${o.id}`)}>
                    <TableCell className="font-medium">{o.name}</TableCell>
                    <TableCell className="text-sm">{accounts.find((a) => a.id === o.account_id)?.company_name || "—"}</TableCell>
                    <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${stageChipClass(o.stage)}`}>{o.stage}</span></TableCell>
                    <TableCell className="font-semibold">{fmtINR(o.value)}</TableCell>
                    <TableCell>{o.quarter}</TableCell>
                    <TableCell className="text-sm">{fmtDate(o.closure_date)}</TableCell>
                  </TableRow>
                ))}
                {filteredList.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm opacity-60 py-10">No opportunities {stageFilter ? `in stage "${stageFilter}"` : "yet"}.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
